/**
 * Allowlisted Dimensions, Measures, and persisted View/Chart state for
 * Workflow Insights. Shared by client, server, and (later) AI tool schemas.
 */

import { z } from 'zod';

import { WORKFLOW_DATE_FILTER_FIELDS, WORKFLOW_FILTER_FIELDS } from './workflow-filter-fields';

/** Personal vs org-published Workflow Views and Insight Pins. */
export const INSIGHT_SCOPE = {
  Org: 'ORG',
  Personal: 'PERSONAL',
} as const;

export type InsightScope = (typeof INSIGHT_SCOPE)[keyof typeof INSIGHT_SCOPE];

export const InsightScopeSchema = z.enum(INSIGHT_SCOPE);

/**
 * Chart Dimension fields (v1). `assignee` maps to the list `permissions` field;
 * `signedAt` is signing-phase completedAt (not Workflow.completedAt).
 */
export const WORKFLOW_CHART_DIMENSIONS = {
  assignee: WORKFLOW_FILTER_FIELDS.permissions,
  board: WORKFLOW_FILTER_FIELDS.board,
  column: WORKFLOW_FILTER_FIELDS.column,
  columnCategory: WORKFLOW_FILTER_FIELDS.columnCategory,
  completedAt: WORKFLOW_DATE_FILTER_FIELDS.completedAt,
  createdAt: WORKFLOW_DATE_FILTER_FIELDS.createdAt,
  owner: WORKFLOW_FILTER_FIELDS.owner,
  priority: WORKFLOW_FILTER_FIELDS.priority,
  signedAt: WORKFLOW_DATE_FILTER_FIELDS.signedAt,
  startedAt: WORKFLOW_DATE_FILTER_FIELDS.startedAt,
  state: WORKFLOW_FILTER_FIELDS.state,
  workflowType: WORKFLOW_FILTER_FIELDS.workflowType,
} as const;

export type WorkflowChartDimension =
  (typeof WORKFLOW_CHART_DIMENSIONS)[keyof typeof WORKFLOW_CHART_DIMENSIONS];

/** Display names for Dimensions, shared by axis labels and Insights Chart controls. */
export const WORKFLOW_CHART_DIMENSION_LABELS = {
  [WORKFLOW_CHART_DIMENSIONS.assignee]: 'Assignee',
  [WORKFLOW_CHART_DIMENSIONS.board]: 'Board',
  [WORKFLOW_CHART_DIMENSIONS.column]: 'Column',
  [WORKFLOW_CHART_DIMENSIONS.columnCategory]: 'Status type',
  [WORKFLOW_CHART_DIMENSIONS.completedAt]: 'Completed at',
  [WORKFLOW_CHART_DIMENSIONS.createdAt]: 'Created at',
  [WORKFLOW_CHART_DIMENSIONS.owner]: 'Requester',
  [WORKFLOW_CHART_DIMENSIONS.priority]: 'Priority',
  [WORKFLOW_CHART_DIMENSIONS.signedAt]: 'Signed at',
  [WORKFLOW_CHART_DIMENSIONS.startedAt]: 'Started at',
  [WORKFLOW_CHART_DIMENSIONS.state]: 'Signing status',
  [WORKFLOW_CHART_DIMENSIONS.workflowType]: 'Workflow type',
} as const satisfies Record<WorkflowChartDimension, string>;

/** Plural Dimension names, for controls that count a whole catalog of buckets. */
export const WORKFLOW_CHART_DIMENSION_PLURAL_LABELS = {
  [WORKFLOW_CHART_DIMENSIONS.assignee]: 'Assignees',
  [WORKFLOW_CHART_DIMENSIONS.board]: 'Boards',
  [WORKFLOW_CHART_DIMENSIONS.column]: 'Columns',
  [WORKFLOW_CHART_DIMENSIONS.columnCategory]: 'Status types',
  [WORKFLOW_CHART_DIMENSIONS.completedAt]: 'Completed dates',
  [WORKFLOW_CHART_DIMENSIONS.createdAt]: 'Created dates',
  [WORKFLOW_CHART_DIMENSIONS.owner]: 'Requesters',
  [WORKFLOW_CHART_DIMENSIONS.priority]: 'Priorities',
  [WORKFLOW_CHART_DIMENSIONS.signedAt]: 'Signed dates',
  [WORKFLOW_CHART_DIMENSIONS.startedAt]: 'Started dates',
  [WORKFLOW_CHART_DIMENSIONS.state]: 'Signing statuses',
  [WORKFLOW_CHART_DIMENSIONS.workflowType]: 'Workflow types',
} as const satisfies Record<WorkflowChartDimension, string>;

export const WORKFLOW_CHART_DIMENSION_VALUES: WorkflowChartDimension[] =
  Object.values(WORKFLOW_CHART_DIMENSIONS);

/**
 * Dimensions bucketed by date. Their axis is ordered rather than categorical, so
 * a reader can infer skipped tick labels from the surrounding ones.
 */
export const WORKFLOW_CHART_DATE_DIMENSIONS = [
  WORKFLOW_CHART_DIMENSIONS.completedAt,
  WORKFLOW_CHART_DIMENSIONS.createdAt,
  WORKFLOW_CHART_DIMENSIONS.signedAt,
  WORKFLOW_CHART_DIMENSIONS.startedAt,
] as const satisfies readonly WorkflowChartDimension[];

export function getIsWorkflowChartDateDimension(
  value: string
): value is (typeof WORKFLOW_CHART_DATE_DIMENSIONS)[number] {
  return WORKFLOW_CHART_DATE_DIMENSIONS.some(dimension => dimension === value);
}

export const WorkflowChartDimensionSchema = z.enum(WORKFLOW_CHART_DIMENSIONS);

export function getIsWorkflowChartDimension(value: string): value is WorkflowChartDimension {
  return WORKFLOW_CHART_DIMENSION_VALUES.some(dimension => dimension === value);
}

/**
 * Whether the Workflows list can group rows by this Dimension. Adding a
 * Dimension to `WORKFLOW_CHART_DIMENSIONS` is a type error until this Record
 * (and the label Records above) get an entry.
 */
export const WORKFLOW_CHART_DIMENSION_GROUPABLE = {
  [WORKFLOW_CHART_DIMENSIONS.assignee]: true,
  [WORKFLOW_CHART_DIMENSIONS.board]: true,
  [WORKFLOW_CHART_DIMENSIONS.column]: true,
  [WORKFLOW_CHART_DIMENSIONS.columnCategory]: true,
  [WORKFLOW_CHART_DIMENSIONS.completedAt]: false,
  [WORKFLOW_CHART_DIMENSIONS.createdAt]: false,
  [WORKFLOW_CHART_DIMENSIONS.owner]: true,
  [WORKFLOW_CHART_DIMENSIONS.priority]: true,
  [WORKFLOW_CHART_DIMENSIONS.signedAt]: false,
  [WORKFLOW_CHART_DIMENSIONS.startedAt]: false,
  [WORKFLOW_CHART_DIMENSIONS.state]: true,
  [WORKFLOW_CHART_DIMENSIONS.workflowType]: true,
} as const satisfies Record<WorkflowChartDimension, boolean>;

export type WorkflowListGroupableDimension = {
  [K in WorkflowChartDimension]: (typeof WORKFLOW_CHART_DIMENSION_GROUPABLE)[K] extends true
    ? K
    : never;
}[WorkflowChartDimension];

export const WORKFLOW_LIST_GROUPABLE_DIMENSIONS: WorkflowListGroupableDimension[] =
  WORKFLOW_CHART_DIMENSION_VALUES.filter(
    (dimension): dimension is WorkflowListGroupableDimension =>
      WORKFLOW_CHART_DIMENSION_GROUPABLE[dimension]
  );

export function getIsWorkflowListGroupableDimension(
  value: string
): value is WorkflowListGroupableDimension {
  return getIsWorkflowChartDimension(value) && WORKFLOW_CHART_DIMENSION_GROUPABLE[value];
}

export const WORKFLOW_CHART_MEASURES = {
  count: 'count',
  medianDurationDays: 'medianDurationDays',
} as const;

export type WorkflowChartMeasure =
  (typeof WORKFLOW_CHART_MEASURES)[keyof typeof WORKFLOW_CHART_MEASURES];

export const WORKFLOW_CHART_MEASURE_VALUES: WorkflowChartMeasure[] =
  Object.values(WORKFLOW_CHART_MEASURES);

/**
 * Display names for Measures, including units. Shared by the server series
 * labels, Chart value axes, and the Workflows list aggregation footer.
 */
export const WORKFLOW_CHART_MEASURE_LABELS = {
  [WORKFLOW_CHART_MEASURES.count]: 'Count',
  [WORKFLOW_CHART_MEASURES.medianDurationDays]: 'Typical time (days)',
} as const satisfies Record<WorkflowChartMeasure, string>;

export const WorkflowChartMeasureSchema = z.enum(WORKFLOW_CHART_MEASURES);

export function getIsWorkflowChartMeasure(value: string): value is WorkflowChartMeasure {
  return WORKFLOW_CHART_MEASURE_VALUES.some(measure => measure === value);
}

/**
 * Chart types a Chart can render as. Names follow MUI X, where `column` is vertical
 * bars and `bar` is horizontal.
 */
export const WORKFLOW_CHART_TYPES = {
  area: 'area',
  bar: 'bar',
  column: 'column',
  line: 'line',
  pie: 'pie',
} as const;

export type WorkflowChartType = (typeof WORKFLOW_CHART_TYPES)[keyof typeof WORKFLOW_CHART_TYPES];

export const WORKFLOW_CHART_TYPE_VALUES: WorkflowChartType[] = Object.values(WORKFLOW_CHART_TYPES);

export const DEFAULT_WORKFLOW_CHART_TYPE = WORKFLOW_CHART_TYPES.column;

export function getIsWorkflowChartType(value: string): value is WorkflowChartType {
  return WORKFLOW_CHART_TYPE_VALUES.some(chartType => chartType === value);
}

export const WorkflowChartTypeSchema = z.enum(WORKFLOW_CHART_TYPES);

/** Sentinel Dimension bucket keys (shared by Chart series + list drill-down). */
export const WORKFLOW_CHART_EMPTY_BUCKET_KEYS = {
  assignee: '__unassigned__',
  columnCategory: '__no_status_type__',
  date: '__no_date__',
  priority: '__no_priority__',
  state: '__no_signing_status__',
  workflowType: '__no_workflow_type__',
} as const;

/**
 * Display label for Workflows with no Workflow-type template.
 * Shared by the Workflows list, chart Dimension buckets, and drill-down filters.
 */
export const CUSTOM_WORKFLOW_TYPE_LABEL = 'Custom';

/**
 * Named categorical buckets on the compact Insights surface (axis or legend).
 * Insights adds a trailing Other remainder for additive Measures.
 */
export const INSIGHT_CHART_CATEGORY_LIMIT = 7;

export function getInsightDashboardSeriesLimits(options: {
  dimensions: readonly string[];
  chartType?: string;
}): {
  primaryLimit?: number;
  secondaryLimit?: number;
} {
  const primary = options.dimensions.at(0);
  return {
    primaryLimit:
      primary && !getIsWorkflowChartDateDimension(primary)
        ? INSIGHT_CHART_CATEGORY_LIMIT
        : undefined,
    secondaryLimit: options.dimensions.at(1) ? INSIGHT_CHART_CATEGORY_LIMIT : undefined,
  };
}

/**
 * Series id for leftover buckets a limited Chart could not plot (legend series
 * or category-axis tick). It rolls up only what the plot leaves out, so counts
 * still sum to the full cohort; every bucket stays in the catalog. Only emitted
 * for additive Measures, where a remainder is arithmetically meaningful.
 */
export const WORKFLOW_CHART_HIDDEN_SERIES_KEY = '__other__';

export const WORKFLOW_CHART_DATE_BUCKETS = {
  day: 'day',
  month: 'month',
  week: 'week',
} as const;

export type WorkflowChartDateBucket =
  (typeof WORKFLOW_CHART_DATE_BUCKETS)[keyof typeof WORKFLOW_CHART_DATE_BUCKETS];

export const DEFAULT_WORKFLOW_CHART_DATE_BUCKET = WORKFLOW_CHART_DATE_BUCKETS.week;

export const DEFAULT_WORKFLOW_CHART_MIN_SAMPLE_SIZE = 2;

export const WORKFLOW_CHART_GRAINS = {
  columnInterval: 'columnInterval',
  workflow: 'workflow',
} as const;

export type WorkflowChartGrain = (typeof WORKFLOW_CHART_GRAINS)[keyof typeof WORKFLOW_CHART_GRAINS];

export const WorkflowChartGrainSchema = z.enum(WORKFLOW_CHART_GRAINS);

export function getIsWorkflowChartGrain(value: string): value is WorkflowChartGrain {
  return Object.values(WORKFLOW_CHART_GRAINS).some(grain => grain === value);
}

const FilterModelItemsSchema = z.object({
  items: z.array(
    z
      .object({
        field: z.string(),
        operator: z.string(),
      })
      .passthrough()
  ),
});

const EMPTY_DATE_OPERATOR = 'isEmpty';

function getFilterModelSelectsCompletedAt(filterModel: unknown): boolean {
  const parsed = FilterModelItemsSchema.safeParse(filterModel);
  if (!parsed.success) return false;
  return parsed.data.items.some(
    item =>
      item.field === WORKFLOW_DATE_FILTER_FIELDS.completedAt &&
      item.operator !== EMPTY_DATE_OPERATOR
  );
}

/**
 * Status-dimension Charts over a completed cohort group by historical stays.
 * Current status of completed work is a single terminal column.
 */
export function getResolvedWorkflowChartGrain(options: {
  dimensions: readonly string[];
  completed?: boolean | null;
  filterModel?: unknown;
  grain?: string | null;
  measure?: string | null;
}): WorkflowChartGrain {
  const { completed, dimensions, filterModel, grain, measure } = options;
  if (grain && getIsWorkflowChartGrain(grain)) return grain;
  if (!dimensions.includes(WORKFLOW_CHART_DIMENSIONS.column)) {
    return WORKFLOW_CHART_GRAINS.workflow;
  }
  if (measure === WORKFLOW_CHART_MEASURES.medianDurationDays) {
    return WORKFLOW_CHART_GRAINS.columnInterval;
  }
  if (completed === true || getFilterModelSelectsCompletedAt(filterModel)) {
    return WORKFLOW_CHART_GRAINS.columnInterval;
  }
  return WORKFLOW_CHART_GRAINS.workflow;
}

export const WorkflowChartDateBucketSchema = z.enum(WORKFLOW_CHART_DATE_BUCKETS);

export function getIsWorkflowChartDateBucket(value: string): value is WorkflowChartDateBucket {
  return Object.values(WORKFLOW_CHART_DATE_BUCKETS).some(bucket => bucket === value);
}

/** Max Dimensions on one Chart in v1 (primary + optional secondary / stack). */
export const MAX_WORKFLOW_CHART_DIMENSIONS = 2;

const GridChartsIntegrationItemSchema = z.union([
  z.string().min(1),
  z.object({
    field: z.string().min(1),
    hidden: z.boolean().optional(),
  }),
]);

const GridChartsIntegrationChartSchema = z.object({
  chartType: WorkflowChartTypeSchema.optional().catch(undefined),
  configuration: z
    .record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.null()]))
    .optional(),
  /** Optional date bucket for date Dimensions (day / week / month). */
  dateBucket: WorkflowChartDateBucketSchema.optional(),
  dimensions: z.array(GridChartsIntegrationItemSchema).optional(),
  grain: WorkflowChartGrainSchema.optional(),
  insightValues: z
    .array(
      z.object({
        dateDimension: WorkflowChartDimensionSchema.optional(),
        filterModel: z.unknown().optional(),
        id: z.string().min(1),
        label: z.string().optional(),
        measure: WorkflowChartMeasureSchema,
      })
    )
    .max(2)
    .optional(),
  minSampleSize: z.number().int().positive().optional(),
  /** User-facing title for this Chart within a multi-Chart View. */
  title: z.string().trim().min(1).optional(),
  values: z.array(GridChartsIntegrationItemSchema).optional(),
});

/**
 * Persistable `chartsIntegration` slice of `GridInitialStatePremium`.
 * Series datasets are never stored — only field picks / type / configuration.
 */
export const WorkflowChartsIntegrationStateSchema = z.object({
  activeChartId: z.string().optional(),
  charts: z.record(z.string(), GridChartsIntegrationChartSchema).optional(),
});

/**
 * Chart-relevant Workflow View state. Intentionally omits scroll, density,
 * pagination, sidebar, and other UI chrome.
 */
export const WorkflowViewStateSchema = z.object({
  aggregation: z
    .object({
      model: z.record(z.string(), z.string()).optional(),
    })
    .optional(),
  chartsIntegration: WorkflowChartsIntegrationStateSchema.optional(),
  filter: z
    .object({
      filterModel: z.unknown().optional(),
    })
    .optional(),
  rowGrouping: z
    .object({
      model: z.array(z.string()).max(MAX_WORKFLOW_CHART_DIMENSIONS).optional(),
    })
    .optional(),
  sorting: z
    .object({
      sortModel: z
        .array(
          z.object({
            field: z.string(),
            sort: z.enum(['asc', 'desc']).nullable().optional(),
          })
        )
        .optional(),
    })
    .optional(),
  /** Optional Workflow list scope filter (completed / published / variant). */
  workflowFilter: z
    .object({
      completed: z.boolean().optional(),
      published: z.boolean().optional(),
      variant: z.enum(['Contract', 'Template']).optional(),
    })
    .optional(),
});

export type WorkflowViewState = z.infer<typeof WorkflowViewStateSchema>;

export function parseWorkflowViewState(value: unknown): WorkflowViewState {
  return WorkflowViewStateSchema.parse(value ?? {});
}

export function getIsWorkflowViewState(value: unknown): value is WorkflowViewState {
  return WorkflowViewStateSchema.safeParse(value ?? {}).success;
}

/** Insights pin layout (grid placement on the Insights board). */
export const InsightPinLayoutSchema = z.object({
  h: z.number().int().positive(),
  w: z.number().int().positive(),
  x: z.number().int().nonnegative(),
  y: z.number().int().nonnegative(),
});

export type InsightPinLayout = z.infer<typeof InsightPinLayoutSchema>;

/** Default 1× layout slot for a newly pinned Chart on Insights. */
export const DEFAULT_INSIGHT_PIN_LAYOUT = {
  h: 2,
  w: 4,
  x: 0,
  y: 0,
} as const satisfies InsightPinLayout;

export function parseInsightPinLayout(value: unknown): InsightPinLayout {
  return InsightPinLayoutSchema.parse(value);
}

export const WorkflowChartSeriesValueSchema = z.object({
  dateDimension: WorkflowChartDimensionSchema.optional(),
  filterModel: z.unknown().optional(),
  id: z.string().min(1),
  label: z.string().optional(),
  measure: WorkflowChartMeasureSchema,
});

export type WorkflowChartSeriesValue = z.infer<typeof WorkflowChartSeriesValueSchema>;

/**
 * Request for server Chart series. Dimensions are ordered (primary, secondary).
 * Date Dimensions use `dateBucket` (default week).
 */
export const WorkflowChartSeriesRequestSchema = z
  .object({
    dateBucket: WorkflowChartDateBucketSchema.default(DEFAULT_WORKFLOW_CHART_DATE_BUCKET),
    dimensions: z.array(WorkflowChartDimensionSchema).min(1).max(MAX_WORKFLOW_CHART_DIMENSIONS),
    filterModel: z.unknown().optional(),
    grain: WorkflowChartGrainSchema.optional(),
    measure: WorkflowChartMeasureSchema,
    minSampleSize: z.number().int().positive().default(DEFAULT_WORKFLOW_CHART_MIN_SAMPLE_SIZE),
    primaryKeys: z.array(z.string().min(1)).max(INSIGHT_CHART_CATEGORY_LIMIT).optional(),
    primaryLimit: z.number().int().positive().max(INSIGHT_CHART_CATEGORY_LIMIT).optional(),
    secondaryKeys: z.array(z.string().min(1)).max(INSIGHT_CHART_CATEGORY_LIMIT).optional(),
    secondaryLimit: z.number().int().positive().max(INSIGHT_CHART_CATEGORY_LIMIT).optional(),
    values: z.array(WorkflowChartSeriesValueSchema).min(1).max(2).optional(),
  })
  .superRefine((value, ctx) => {
    const dateDims = value.dimensions.filter(getIsWorkflowChartDateDimension);
    if (dateDims.length > 1) {
      ctx.addIssue({
        code: 'custom',
        message: 'At most one date Dimension is allowed per Chart',
        path: ['dimensions'],
      });
    }
  });

export type WorkflowChartSeriesRequest = z.infer<typeof WorkflowChartSeriesRequestSchema>;
export type WorkflowChartSeriesRequestInput = z.input<typeof WorkflowChartSeriesRequestSchema>;

export function parseWorkflowChartSeriesRequest(value: unknown): WorkflowChartSeriesRequest {
  const parsed = WorkflowChartSeriesRequestSchema.parse(value);
  return {
    ...parsed,
    grain: getResolvedWorkflowChartGrain({
      dimensions: parsed.dimensions,
      filterModel: parsed.filterModel,
      grain: parsed.grain,
      measure: parsed.measure,
    }),
  };
}

const MS_PER_DAY = 1000 * 60 * 60 * 24;

/**
 * Cycle time in days: start is `startedAt ?? createdAt`, end is `completedAt ?? now`.
 * Charts restrict to completed work only when the View already filters that way.
 */
export function getWorkflowCycleTimeDays(options: {
  completedAt: Date | string | null;
  createdAt: Date | string;
  startedAt: Date | string | null;
  now?: Date;
}): number | null {
  const start = new Date(options.startedAt ?? options.createdAt).getTime();
  const end = options.completedAt
    ? new Date(options.completedAt).getTime()
    : (options.now ?? new Date()).getTime();
  const ms = end - start;
  if (!Number.isFinite(ms) || ms < 0) return null;
  return ms / MS_PER_DAY;
}
